import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

/**
 * The collection grid — Load More and the "You've viewed X of Y products" counter.
 *
 * It extends `Component` rather than the theme's `PaginatedList`, which is what `main-collection`
 * and `search-results` use, for two reasons:
 *
 * 1. `PaginatedList` IS infinite scroll. Its whole mechanism is an `IntersectionObserver` over two
 *    sentinel `<span>`s, and the method that appends a page — `#renderNextPage()` — is hard-private
 *    (`#`), so it can be neither called nor overridden from a subclass. Reaching it would mean
 *    editing `assets/paginated-list.js`, which serves two sections that are live in production.
 *
 * 2. The control here is a real `<a href="?page=N">`. Googlebot does not scroll and does not press
 *    buttons, so the original needed a separate hidden pager to give crawlers a path through the
 *    collection; here the link the shopper presses is that path. `loadMore` swallows the click so
 *    a shopper with JavaScript never navigates.
 *
 * `sections/sl-blog.liquid` + `assets/sl-blog.js` solved the same problem first; this is the same
 * shape, minus the blog's reveal-half-a-page step (the grid appends whole server pages).
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} grid - The `<ul>` of cards.
 * @property {HTMLElement} counter - The "You've viewed X of Y" line.
 * @property {HTMLAnchorElement} button - The Load More link.
 * @property {HTMLElement} more - The block holding both, and the pagination data attributes.
 * @property {HTMLElement} [progress] - The 2026 design's progress bar fill (optional).
 * @property {HTMLElement} [toolbar] - The 2026 design's Filter / Sort bar, sticky below 1024px.
 * @property {HTMLElement} [toolbarSentinel] - A 1px marker just above the bar (optional).
 * @property {HTMLButtonElement} [filterButton] - The bar's Filter button, which opens the drawer.
 *
 * @extends {Component<Refs>}
 */
export class SlCollectionGridComponent extends Component {
  /** @type {string[]} */
  requiredRefs = ['grid', 'counter', 'button', 'more'];

  /** The highest server page whose cards are in the DOM. */
  #loadedPage = 1;

  /** `paginate.pages`. */
  #pages = 1;

  /** The filtered total — `paginate.items`, not `collection.products_count`. */
  #total = 0;

  /** Page number -> parsed cards, filled by the idle prefetch. @type {Map<number, Element[]>} */
  #prefetched = new Map();

  /** True while a fetch is in flight, so a double press cannot double-append. */
  #busy = false;

  /** @type {AbortController} */
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#queueInit();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#stuckObserver?.disconnect();
    this.#morphObserver?.disconnect();
  }

  /**
   * Before DEC-036 (docs/TECH_DEBT.md → TD-30) `updatedCallback` never ran after a Section Rendering
   * re-render: assets/morph.js called `onAfterUpdate(newNode)` with the node parsed from the
   * response, which is not an upgraded custom element, so its `instanceof Component` check never
   * passed. A filter or sort change left this component with the previous result set's total, page
   * count and loaded page — Load More then fetched the wrong page and the counter stayed blank.
   *
   * So the component also watches its own markup: the morph rewrites the Load More block's
   * pagination attributes and the grid's children, and either means "new result set". The
   * component's own appends are discarded with `takeRecords()` right after they happen.
   *
   * With the morph fix both signals arrive for the same re-render, so both go through `#queueInit`,
   * which runs `#init` once per microtask: twice would re-bind every listener and schedule the next
   * page's idle prefetch twice. The observer can go once DEC-036 has been live for a while.
   */
  #morphObserver = /** @type {MutationObserver | null} */ (null);

  #initQueued = false;

  #queueInit() {
    if (this.#initQueued) return;
    this.#initQueued = true;
    queueMicrotask(() => {
      this.#initQueued = false;
      if (this.isConnected) this.#init();
    });
  }

  #observeMorphs() {
    this.#morphObserver?.disconnect();
    const { more, grid } = this.refs;
    if (!more || !grid) return;

    this.#morphObserver ??= new MutationObserver(() => {
      this.#morphObserver?.takeRecords();
      this.#queueInit();
    });
    this.#morphObserver.observe(more, { attributes: true, attributeFilter: ['data-total', 'data-pages', 'data-page'] });
    this.#morphObserver.observe(grid, { childList: true });
  }

  // ------------------------------------------------------------------ setup

  #init() {
    this.#abort.abort();
    this.#abort = new AbortController();

    // A filter change re-renders this whole section, so anything buffered was buffered for the
    // previous result set. Same reason `paginated-list.js` clears its own page cache on this event.
    this.#prefetched = new Map();
    this.#busy = false;

    const { more } = this.refs;
    this.#total = readNumber(more.dataset.total, 0);
    this.#pages = readNumber(more.dataset.pages, 1);
    this.#loadedPage = readNumber(more.dataset.page, 1);

    this.#render();
    this.#schedulePrefetch();
    this.#observeToolbar();
    this.#observeMorphs();

    this.#restoreTagFocus();

    // A filter or sort change re-renders the section; in the 2026 design the new first cards rise
    // in, as they do after Load More. Flagged here, played on the re-init that follows it.
    if (this.#riseAfterUpdate) {
      this.#riseAfterUpdate = false;
      this.#revealAppended(this.#productCards.slice(0, 8), { focus: false });
    }

    // Facets re-render the whole section, which comes back through `updatedCallback`; this only has
    // to drop the buffer early, before the new markup lands. Registered against the controller that
    // `#init` just replaced, so a re-render never stacks a second listener.
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      () => {
        this.#prefetched = new Map();
        this.#riseAfterUpdate = true;
      },
      { signal: this.#abort.signal }
    );

    // Removing an applied filter re-renders the section and the pressed button is gone: remember
    // where it was, so focus can land on the tag that takes its place (see #restoreTagFocus).
    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        const remove = event.target.closest('.sl-applied-tag__remove');
        const list = remove?.closest('.sl-collection-filters__tags, .sl-collection-toolbar__tags');
        if (!remove || !list) return;
        const buttons = Array.from(list.querySelectorAll('.sl-applied-tag__remove'));
        this.#pendingTagFocus = {
          list: list.classList.contains('sl-collection-toolbar__tags') ? 'toolbar' : 'sidebar',
          index: buttons.indexOf(remove),
        };
      },
      { signal: this.#abort.signal }
    );

    // Closing the filter drawer hands focus back to the Filter button. The native dialog would
    // restore the element focused before it opened, but iOS does not focus a tapped button, so
    // that is usually <body>. dialog.js's close event does not bubble: listened for on the drawer.
    this.querySelector('.sl-filters-drawer')?.addEventListener(
      'dialog:close',
      () => this.refs.filterButton?.focus({ preventScroll: true }),
      { signal: this.#abort.signal }
    );

    // The 2026 card's "Added" state. Listened for on this component, not per card: cart events
    // bubble from each card's form, and cards appended by Load More are covered without re-binding.
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal: this.#abort.signal });
  }

  // -------------------------------------------------- 2026 toolbar shadow

  /** Set by a filter change, consumed by the re-render that follows it. */
  #riseAfterUpdate = false;

  /** Where the removed applied-filter tag was. @type {{ list: 'toolbar' | 'sidebar', index: number } | null} */
  #pendingTagFocus = null;

  /**
   * After a tag removal: focus the tag now at the same place (or the last one), and the sort
   * control when no tag is left — so keyboard focus is not dropped on <body> by the re-render.
   */
  #restoreTagFocus() {
    const pending = this.#pendingTagFocus;
    if (!pending) return;
    this.#pendingTagFocus = null;

    const listSelector =
      pending.list === 'toolbar' ? '.sl-collection-toolbar__tags' : '.sl-collection-filters__tags';
    const buttons = Array.from(this.querySelectorAll(`${listSelector} .sl-applied-tag__remove`));
    const target =
      buttons[Math.min(pending.index, buttons.length - 1)] ?? this.querySelector('.sl-collection-toolbar__select');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /** @type {IntersectionObserver | null} */
  #stuckObserver = null;

  /**
   * Below 1024px the Filter / Sort bar sticks under the site header; it only casts its shadow while
   * it is stuck. The 1px sentinel above it leaves the viewport (minus the header) exactly when the
   * bar starts sticking. Re-armed on every re-render, because the morph may replace both nodes.
   */
  #observeToolbar() {
    this.#stuckObserver?.disconnect();
    this.#stuckObserver = null;

    const { toolbar, toolbarSentinel } = this.refs;
    if (!toolbar || !toolbarSentinel) return;

    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    this.#stuckObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        toolbar.classList.toggle('is-stuck', !entry.isIntersecting && entry.boundingClientRect.top < headerHeight + 1);
      },
      { rootMargin: `-${Math.round(headerHeight) + 1}px 0px 0px 0px` }
    );
    this.#stuckObserver.observe(toolbarSentinel);
  }

  // ------------------------------------------------------- card add states

  /** How long the 2026 card's "+" stays green after a successful add (Sasha's "Added" step). */
  static ADDED_STATE_MS = 1200;

  /** One reset timer per button, so a second add restarts the green state instead of cutting it. @type {WeakMap<HTMLElement, number>} */
  #addedTimers = new WeakMap();

  /**
   * Marks the 2026 card's "+" as added — only here, on a `cart:update` that reports no error. The
   * click handler in product-form.js runs before the request, so anything set there would show
   * success for an add that then fails (memory/failed-add-looked-successful.md). While the request
   * runs, product-form.js disables the button, which is the "Adding…" state in the card's CSS.
   *
   * @param {Event & {detail?: {data?: {didError?: boolean}}}} event
   */
  #handleCartUpdate = (event) => {
    if (!(event.target instanceof Element)) return;
    const form = event.target.closest('.sl-collection-card__form');
    if (!form || event.detail?.data?.didError) return;

    const button = form.querySelector('.sl-collection-card__add');
    if (!(button instanceof HTMLElement)) return;

    clearTimeout(this.#addedTimers.get(button));
    button.dataset.state = 'added';
    this.#addedTimers.set(
      button,
      window.setTimeout(() => button.removeAttribute('data-state'), SlCollectionGridComponent.ADDED_STATE_MS)
    );
  };

  /**
   * The product cards, live, read off the grid rather than through `ref="cards[]"`: the base class
   * refreshes refs through a MutationObserver, so straight after an append `this.refs.cards` is
   * still the pre-append list. Filler cards (Need help, metaobject cards) are excluded — the counter
   * promises products, and counting the fillers would overstate it.
   *
   * @returns {Element[]}
   */
  get #productCards() {
    return Array.from(this.refs.grid.querySelectorAll('[data-card-type="product"]'));
  }

  // ----------------------------------------------------------------- render

  #render() {
    this.#updateCounter();

    // Nothing left to fetch.
    this.refs.button.hidden = this.#loadedPage >= this.#pages;
  }

  #updateCounter() {
    const shown = Math.min(this.#productCards.length, this.#total);
    const template = this.refs.more.dataset.counterTemplate ?? '';

    this.refs.counter.textContent = template
      .replace('__SHOWN__', String(shown))
      .replace('__TOTAL__', String(this.#total));

    // The 2026 design's bar says the same as the counter, as a share (it is aria-hidden).
    const { progress } = this.refs;
    if (progress && this.#total > 0) progress.style.inlineSize = `${(shown / this.#total) * 100}%`;

    // The sidebar's "Showing x of y" lives inside the facets form (another component), so it is
    // found by attribute rather than by ref.
    for (const line of this.querySelectorAll('[data-sl-shown-template]')) {
      const lineTemplate = line.getAttribute('data-sl-shown-template') ?? '';
      line.textContent = lineTemplate.replace('__SHOWN__', String(shown)).replace('__TOTAL__', String(this.#total));
    }
  }

  // ------------------------------------------------------------------ action

  /**
   * Declarative `on:click="/loadMore"`.
   *
   * The control is an `<a href="?page=N">` so crawlers have a path through the collection, which
   * means the click has to be swallowed here or the browser would navigate instead of appending.
   * `component.js` passes the event as the last argument; it is optional only so a direct call
   * from the console still works.
   *
   * @param {Event} [event]
   */
  async loadMore(event) {
    event?.preventDefault();

    if (this.#busy) return;
    if (this.#loadedPage >= this.#pages) return;

    const next = this.#loadedPage + 1;
    this.#busy = true;
    this.refs.grid.setAttribute('aria-busy', 'true');

    try {
      const cards = this.#prefetched.get(next) ?? (await this.#fetchPage(next));
      if (this.#abort.signal.aborted) return;

      // A page that should exist came back empty or failed (a 429, a network error). Returning here
      // left a button that did nothing — a dead click. The link's own href is page N, so follow it:
      // the shopper still gets the products, as a full page load instead of an append.
      if (!cards?.length) {
        const href = this.refs.button.getAttribute('href');
        if (href) window.location.assign(href);
        return;
      }

      const appended = cards.map((card) => card.cloneNode(true));
      this.refs.grid.append(...appended);
      // Our own append, not a re-render: drop it before the morph observer sees it.
      this.#morphObserver?.takeRecords();
      this.#loadedPage = next;
      this.#revealAppended(appended);

      // MANDATORY after any append of product cards. `assets/apply-discount-cards.js` rewrites
      // prices in place from the `data-*` the card carries; without this call the cards that just
      // arrived show list price next to discounted ones from the first page.
      // Precedent: `assets/paginated-list.js` does the same after its own append.
      window.Theme?._applyDiscountToCards?.(this.refs.grid);

      // The URL follows the deepest page loaded, which is what the theme's infinite scroll already
      // does. `replaceState`, not `pushState`: the shopper pressed a button, not a link, so Back
      // should leave the collection rather than walk back through the pages they revealed.
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(next));
      window.history.replaceState(window.history.state, '', url.toString());

      // Keep the crawl path honest: the link now points at the page after the one just added. On
      // the last page the attribute is REMOVED rather than emptied — `href=""` resolves to the
      // current URL, so a stray click would reload the page and lose the shopper's place.
      if (next + 1 <= this.#pages) {
        const nextHref = new URL(window.location.href);
        nextHref.searchParams.set('page', String(next + 1));
        this.refs.button.href = nextHref.toString();
      } else {
        this.refs.button.removeAttribute('href');
      }

      this.#render();
      this.#schedulePrefetch();

      this.dispatchEvent(new CustomEvent('sl-collection-grid:append', { bubbles: true }));
    } finally {
      this.#busy = false;
      this.refs.grid.removeAttribute('aria-busy');
    }
  }

  /**
   * After an append: in the 2026 design the first 8 new cards rise into place 40ms apart (the CSS
   * only animates under `prefers-reduced-motion: no-preference`), and in every design keyboard
   * focus moves to the first new product, so a keyboard user lands on what they asked for instead
   * of on a button that just moved further down the page.
   *
   * @param {Element[]} appended
   * @param {{ focus?: boolean }} [options] - `focus: false` after a filter change, where focus
   *   must stay on the control the shopper just used.
   */
  #revealAppended(appended, { focus = true } = {}) {
    if (this.dataset.design === 'collection_2026') {
      appended.slice(0, 8).forEach((item, index) => {
        if (!(item instanceof HTMLElement)) return;
        item.style.setProperty('--sl-rise-delay', `${index * 40}ms`);
        item.classList.add('sl-collection-grid__item--rise');
      });
    }

    if (!focus) return;
    const firstProduct = appended.find((item) => item.getAttribute('data-card-type') === 'product');
    const link = firstProduct?.querySelector('a[href]');
    if (link instanceof HTMLElement) link.focus({ preventScroll: true });
  }

  // ------------------------------------------------------------------ fetch

  /** Pull the page after the last one loaded while the main thread is idle, so the next press
   *  lands on the buffer instead of on the network. */
  #schedulePrefetch() {
    const next = this.#loadedPage + 1;
    if (next > this.#pages || this.#prefetched.has(next)) return;

    requestIdleCallback(() => {
      this.#fetchPage(next).catch(() => {
        // A failed prefetch is not the shopper's problem: the press-time fetch retries and reports.
      });
    });
  }

  /**
   * @param {number} page
   * @returns {Promise<Element[]>}
   */
  async #fetchPage(page) {
    const cached = this.#prefetched.get(page);
    if (cached) return cached;

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      if (this.#abort.signal.aborted) return [];

      // A detached document, not a reach into the live page: the cards have to come out of the
      // response somehow, and refs cannot see a document that is not attached.
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = Array.from(doc.querySelectorAll('.product-grid > [data-card-type]'));

      if (items.length) this.#prefetched.set(page, items);
      return items;
    } catch (error) {
      console.error(`sl-collection-grid: could not load page ${page}`, error);
      return [];
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('sl-collection-grid: the section-id attribute is required');
    return id;
  }
}

/**
 * @param {string | undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function readNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

if (!customElements.get('sl-collection-grid-component')) {
  customElements.define('sl-collection-grid-component', SlCollectionGridComponent);
}
